/** @jsx jsx */

/** @jsxFrag Fragment */
import { css, jsx } from '@emotion/react';

import { Fragment } from 'react';

import { getLabelForAITranslation, getLabelForAutotranslation, languageTagToLocalName } from 'js/lib/languageLocalized';

import { Button, ContextualHelp, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/xdp';

const useStyles = () => ({
  gapAbove: css`
    margin-top: var(--cds-spacing-200);
  `,
});

type Props = {
  open: boolean;
  onClose: () => void;
  originalLanguage: string;
  autoTranslations: string[];
  humanTranslations: string[];
  subtitles: string[];
  dubbedLanguages: string[];
};
const LanguageInfoDialog = ({
  open,
  onClose,
  originalLanguage,
  autoTranslations,
  humanTranslations,
  subtitles,
  dubbedLanguages,
}: Props) => {
  const styles = useStyles();
  const original = _t('Original');
  const originalLanguageLabel = `${languageTagToLocalName(originalLanguage)} (${original})`;
  const languages = [
    originalLanguageLabel,
    ...humanTranslations.map(languageTagToLocalName),
    ...autoTranslations.map((lang) => `${languageTagToLocalName(lang)} (${getLabelForAutotranslation(lang)})`),
  ];
  const hasTranslatedContent = languages.length > 1;

  const availableLanguagesTitle = (
    <Typography2 component="h3" css={styles.gapAbove} variant="subtitleMedium">
      {_t('Available languages')}
    </Typography2>
  );

  const dubbedLanguageLabels = [
    originalLanguageLabel,
    ...dubbedLanguages.map((lang) => getLabelForAITranslation(lang)),
  ];

  let dialogContent;
  if (dubbedLanguageLabels.length > 1) {
    dialogContent = (
      <>
        <Typography2 component="p" variant="bodyPrimary">
          {_t('"Auto" labels indicate machine-generated translations, "Generated by AI" labels indicate AI dubbing.')}
        </Typography2>
        <div css={styles.gapAbove}>
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('Audio')}
          </Typography2>
        </div>
        <Typography2 component="p" variant="bodyPrimary">
          {dubbedLanguageLabels.join(', ')}
        </Typography2>
        <div css={styles.gapAbove}>
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('Subtitles')}
          </Typography2>
        </div>
        <Typography2 component="p" variant="bodyPrimary">
          {[...languages, ...subtitles.map(languageTagToLocalName)].join(', ')}
        </Typography2>
        <InlineNotification css={styles.gapAbove} severity="information">
          {_t(
            "Any content in this course that's hosted outside of Coursera (such as external websites, plugins, and proctored exams) may only be available in its original language."
          )}
        </InlineNotification>
      </>
    );
  } else {
    dialogContent = (
      <>
        {hasTranslatedContent && (
          <Typography2 component="p" variant="bodyPrimary">
            {_t(
              'You can switch between translations using the language menu after you enroll. Languages tagged as “auto” have been translated automatically by an online translation service. This is a machine based translation service and not carried out by a professional translator.'
            )}
          </Typography2>
        )}

        {hasTranslatedContent && (
          <ContextualHelp
            css={styles.gapAbove}
            helpToggleProps={{
              'aria-label': _t('More information on translated content'),
            }}
            label={availableLanguagesTitle}
          >
            <Typography2 component="p" variant="bodyPrimary">
              {_t('Audio for video lectures may only be available in the original language.')}
            </Typography2>
          </ContextualHelp>
        )}
        {!hasTranslatedContent && availableLanguagesTitle}

        <Typography2 component="p" variant="bodyPrimary">
          {languages.join(', ')}
        </Typography2>

        {subtitles.length > 0 && (
          <>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.gapAbove}>
              {_t('Video subtitles only')}
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary">
              {subtitles.map(languageTagToLocalName).join(', ')}
            </Typography2>
          </>
        )}

        {hasTranslatedContent && (
          <InlineNotification css={styles.gapAbove} severity="information">
            {_t(
              'Automatic translations are limited to instructional content only. Instructional services, support services, and content hosted outside of Coursera (such as external websites,  plugins, and proctored exams) may only be available in their original language.'
            )}
          </InlineNotification>
        )}
        {hasTranslatedContent && (
          <Typography2 component="p" variant="bodyPrimary" css={styles.gapAbove}>
            {_t(
              'Automatic translations have been provided to increase access to instructional content.  Auto translations may contain errors. You can report errors from the language menu to help Coursera improve translations for all learners.'
            )}
          </Typography2>
        )}
      </>
    );
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.HeadingGroup>{_t('Languages')}</Dialog.HeadingGroup>
      <Dialog.Content>{dialogContent}</Dialog.Content>
      <Dialog.Actions>
        <Button variant="primary" size="medium" onClick={onClose}>
          {_t('OK')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export default LanguageInfoDialog;
